/**
 * Who can see and do what, in one place.
 *
 * The role checks used to be written inline wherever they were needed —
 * `user.app_metadata?.roles?.includes('admin')` appears dozens of times across
 * pages, components and the entity layer. That was survivable with one
 * privileged role. It stopped being survivable when `concierge` arrived
 * (2026-09-20, for the browser agent working the inquiry queue), because a
 * missed call site does not fail loudly — it silently shows the wrong person
 * the wrong thing.
 *
 * SECURITY: only `app_metadata.roles` is trusted. `user_metadata` is
 * self-editable by the account holder, and `user.role` is a derived
 * convenience field that can be stale in a cached session. Neither decides
 * access. The server re-checks every one of these against the JWT anyway —
 * these helpers are for rendering, never for enforcement.
 */

/** Roles off the only field that cannot be self-edited. */
export function rolesOf(user) {
  const raw = user?.app_metadata?.roles ?? user?.appMetadata?.roles;
  if (Array.isArray(raw)) return raw;
  return raw ? [raw] : [];
}

export const isAdmin = (user) => rolesOf(user).includes('admin') || user?.role === 'admin';
export const isInvestor = (user) => rolesOf(user).includes('investor') || user?.role === 'investor';
export const isBacker = (user) => rolesOf(user).includes('backer');

/**
 * The desk-only role. Works the inquiry queue, reads the whole booking book,
 * and records the operational facts of a trip (confirmation number, Ticketed,
 * Agency, status, actual cost, seats) — see `canEditTripOps`. Everything else
 * about a booking stays admin-only. Held by the browser agent, not by people;
 * a human teammate should get `admin`.
 */
export const isConcierge = (user) => rolesOf(user).includes('concierge');

/** Read, claim, dismiss and resolve inquiries. */
export const canManageInquiries = (user) => isAdmin(user) || isConcierge(user);

/**
 * See the whole booking book rather than just your own trips. Concierge is
 * included so the desk can read passenger details while working a case.
 *
 * Seeing is not editing — see `canEditBookings`.
 */
export const canViewAllBookings = (user) =>
  isAdmin(user) || isInvestor(user) || isBacker(user) || isConcierge(user);

/**
 * The small set of operational edits on a trip: airline confirmation number,
 * the Ticketed and Agency flags, trip status, what the ticket actually cost
 * us, and seat assignments.
 *
 * Admins plus the concierge agent. Everything else about a booking — fares,
 * passengers, payment plans — stays behind `canEditBookings`. The server
 * enforces the same split at `/api/admin/trip-ops-update`, which names every
 * field it will write. Since 2026-10-27 the concierge cannot set Cancelled or
 * Defaulted there, or move a trip out of Completed, Cancelled or Defaulted
 * (those run side effects only the admin edit form performs); it can set
 * Completed only when the server verifies the trip is paid in full and has
 * an airline confirmation number; and its actual-cost entries are capped
 * against the sold fare (server/trip-ops-status.js).
 */
export const canEditTripOps = (user) => isAdmin(user) || isConcierge(user);

/**
 * READ a trip's internals: everything the trip view shows only to staff —
 * internal comments, who booked it, the full cost breakdown, failed payment
 * attempts, the hotel and insurance ops status, price history, change-flight
 * price comparisons.
 *
 * Admins plus the concierge agent since 2026-10-08 (the founder gave Meta
 * Muse "full view access to trip details"). Seeing only: every control in
 * those panels stays behind `canEditBookings` / `isAdmin`, and the server
 * enforces the same split (`canViewTripInternals` in server.js).
 */
export const canViewTripInternals = (user) => isAdmin(user) || isConcierge(user);

/**
 * Change a booking: the admin edit modal, cancellations, credit, refunds.
 * Deliberately `isAdmin` alone and deliberately separate from
 * `canViewAllBookings`, because view-only is the whole point of the concierge
 * role. The server enforces the same split — `/api/admin/update-booking`
 * checks `isUserAdmin` and was not widened.
 */
export const canEditBookings = (user) => isAdmin(user);
